import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Star, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

function StarRow({ n, size = "w-4 h-4" }) {
  return (
    <span className="inline-flex" aria-label={`${n} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`${size} ${i <= Math.round(n) ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />)}
    </span>
  );
}

const STATUS_MSG = {
  pending: "Your review has been submitted and is awaiting approval.",
  approved: "Your review is live. Thank you!",
  hidden: "Your review is currently not shown.",
  rejected: "Your review was not approved.",
};

function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1" role="radiogroup" aria-label="Your rating" data-testid="review-rating-picker">
      {[1, 2, 3, 4, 5].map((i) => {
        const active = i <= (hover || value);
        return (
          <button key={i} type="button" role="radio" aria-checked={value === i} aria-label={`${i} star${i > 1 ? "s" : ""}`}
            data-testid={`review-rating-${i}`}
            onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(0)}
            onClick={() => onChange(i)}
            className="p-0.5 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#0A192F]">
            <Star className={`w-7 h-7 transition-colors ${active ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} />
          </button>
        );
      })}
    </div>
  );
}

export default function ProductReviews({ slug }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/marketplace/product/${slug}/reviews`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [slug]);
  useEffect(() => { load(); }, [load, user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Please select a star rating");
    if (!title.trim() || !body.trim()) return toast.error("Title and review are required");
    setBusy(true);
    try {
      const { data: res } = await api.post(`/marketplace/product/${slug}/reviews`, { rating, title: title.trim(), body: body.trim() });
      toast.success(res.message || "Thanks! Your review will appear once approved.");
      setRating(0); setTitle(""); setBody("");
      load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Could not submit review"); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const { summary, reviews, can_review, has_reviewed, my_review, authenticated } = data;
  const hasReviews = summary.count > 0;

  return (
    <section className="mt-14 border-t border-slate-200 pt-10" data-testid="product-reviews-section">
      <h2 className="font-display text-2xl uppercase tracking-tight">Customer Reviews</h2>

      {/* Ratings summary + distribution */}
      <div className="mt-5 grid md:grid-cols-[auto_1fr] gap-8 items-start">
        <div className="text-center md:text-left" data-testid="review-summary">
          {hasReviews ? (
            <>
              <div className="font-data text-5xl text-[#0A192F]" data-testid="review-summary-average">{summary.average.toFixed(1)}</div>
              <div className="mt-1"><StarRow n={summary.average} size="w-5 h-5" /></div>
              <div className="text-sm text-slate-500 mt-1" data-testid="review-count">Based on {summary.count} review{summary.count === 1 ? "" : "s"}</div>
            </>
          ) : (
            <div className="text-slate-500" data-testid="review-no-reviews">
              <div className="font-display text-xl uppercase tracking-tight text-[#0A192F]">No reviews yet</div>
              <p className="text-sm mt-1">Be the first to review this product.</p>
            </div>
          )}
        </div>

        {hasReviews && (
          <div className="space-y-1.5 max-w-md" data-testid="review-distribution">
            {[5, 4, 3, 2, 1].map((n) => {
              const cnt = summary.distribution[String(n)] || 0;
              const pct = summary.count ? Math.round((cnt / summary.count) * 100) : 0;
              return (
                <div key={n} className="flex items-center gap-2 text-xs" data-testid={`star-dist-${n}`}>
                  <span className="w-10 text-slate-500 shrink-0">{n} star</span>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-amber-400" style={{ width: `${pct}%` }} /></div>
                  <span className="w-8 text-right text-slate-400 shrink-0">{cnt}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Eligibility / write form */}
      <div className="mt-8">
        {can_review ? (
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-sm p-5 max-w-xl" data-testid="write-review-form">
            <h3 className="font-display uppercase tracking-tight text-[#0A192F]">Write a Review</h3>
            <p className="text-xs text-emerald-700 mt-1 inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Verified purchaser</p>
            <div className="mt-4">
              <label className="label-overline text-[10px] block mb-1.5">Your Rating *</label>
              <StarPicker value={rating} onChange={setRating} />
            </div>
            <div className="mt-4">
              <label htmlFor="rv-title" className="label-overline text-[10px] block mb-1">Title *</label>
              <input id="rv-title" data-testid="review-title-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120}
                placeholder="Sum it up in a few words" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm" />
            </div>
            <div className="mt-3">
              <label htmlFor="rv-body" className="label-overline text-[10px] block mb-1">Your Review *</label>
              <textarea id="rv-body" data-testid="review-body-input" value={body} onChange={(e) => setBody(e.target.value)} rows={4} maxLength={4000}
                placeholder="What did you like or dislike? How was the fit and quality?" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm" />
            </div>
            <button type="submit" disabled={busy} data-testid="review-submit"
              className="mt-4 bg-[#E63946] hover:bg-[#c62f3b] text-white px-6 py-2.5 text-xs font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60">
              {busy ? "Submitting…" : "Submit Review"}
            </button>
          </form>
        ) : has_reviewed && my_review ? (
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 max-w-xl" data-testid="review-my-status">
            <div className="flex items-center gap-2"><StarRow n={my_review.rating} /><span className="font-semibold text-sm text-[#0A192F]">{my_review.title}</span></div>
            <p className="text-sm text-slate-600 mt-1">{STATUS_MSG[my_review.status] || "Your review has been received."}</p>
          </div>
        ) : !authenticated ? (
          <div className="text-sm text-slate-500" data-testid="review-login-prompt">
            <Link to="/login" className="text-[#E63946] font-semibold hover:underline">Log in</Link> to write a review. Only verified purchasers can review this product.
          </div>
        ) : (
          <p className="text-sm text-slate-500" data-testid="review-not-eligible">Only verified purchasers can review this product.</p>
        )}
      </div>

      {/* Approved reviews (newest first) */}
      {hasReviews && (
        <div className="mt-8 space-y-4 max-w-3xl" data-testid="review-list">
          {reviews.map((r) => (
            <div key={r.id} data-testid={`review-item-${r.id}`} className="border-b border-slate-100 pb-4">
              <div className="flex flex-wrap items-center gap-2">
                <StarRow n={r.rating} />
                <span className="font-semibold text-[#0A192F] text-sm">{r.title}</span>
                {r.verified_purchase && <span className="text-[9px] font-bold uppercase text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-sm inline-flex items-center gap-0.5"><ShieldCheck className="w-3 h-3" /> Verified Purchase</span>}
              </div>
              <div className="text-xs text-slate-400 mt-1">{r.reviewer_name} · {(r.created_at || "").slice(0, 10)}</div>
              <p className="text-sm text-slate-600 mt-2 whitespace-pre-line leading-relaxed">{r.body}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
